import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { createHealthMapNote, updateHealthMapNote, deleteHealthMapNote } from "../../../redux/Thunk/HealthMap/HealthMapThunks";
import { setActiveOverlay } from "../../../redux/Reducer/HealthMap/HealthMapSlice";

const HealthMapNoteEditor = ({ record, PID, isNew, onDone, isOwner: isOwnerProp, onImageClick }) => {
    const dispatch = useDispatch();
    const [content, setContent] = useState(record?.data?.content || "");
    const [isEditing, setIsEditing] = useState(isNew || false);
    const [saving, setSaving] = useState(false);
    const [visibility] = useState(record?.data?.visibility || "private");
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(record?.data?.imageUrl || null);
    const [removeImage, setRemoveImage] = useState(false);
    const fileInputRef = React.useRef(null);
    
    const isOwner = isNew || isOwnerProp;

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
            setRemoveImage(false);
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
        setRemoveImage(true);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSave = async (v) => {
        if (!content.trim() && !image && !imagePreview) return;
        const finalVisibility = v || visibility;
        setSaving(true);
        
        const formData = new FormData();
        formData.append("PID", PID);
        formData.append("content", content.trim());
        formData.append("visibility", finalVisibility);
        if (image) {
            formData.append("image", image);
        }
        if (removeImage) {
            formData.append("removeImage", "true");
        }

        try {
            if (isNew) {
                await dispatch(createHealthMapNote(formData));
                setContent("");
                setImage(null);
                setImagePreview(null);
                dispatch(setActiveOverlay(null));
                if (onDone) onDone();
            } else {
                await dispatch(updateHealthMapNote({ id: record.id, formData }));
                setIsEditing(false);
                if (onDone) onDone();
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (isNew) {
            dispatch(setActiveOverlay(null));
            if (onDone) onDone();
        } else {
            setContent(record?.data?.content || "");
            setImagePreview(record?.data?.imageUrl || null);
            setImage(null);
            setRemoveImage(false);
            setIsEditing(false);
        }
    };

    // Read-only view for saved records
    if (!isEditing && !isNew) {
        return (
            <div>
                <div className="hm-card-header" style={{ marginBottom: 12 }}>
                    <span className="hm-card-type record">📝 Clinical Note</span>
                    {record.updatedAt !== record.createdAt && (
                        <span className="hm-card-id">edited</span>
                    )}
                </div>
                {record?.data?.imageUrl && (
                    <div className="hm-record-image-preview" onClick={() => onImageClick && onImageClick(record.data.imageUrl, "Clinical Note", "", record.data.content)}>
                        <img src={record.data.imageUrl} alt="Clinical Attachment" />
                    </div>
                )}
                <div className="hm-card-body" style={{ whiteSpace: "pre-wrap" }}>
                    {record?.data?.content}
                </div>
                <div className="hm-card-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                    <span className={`hm-visibility-badge ${record?.data?.visibility}`}>
                        {record?.data?.visibility === "collaborative" ? "🌐 Collaborative Note" : "🔒 Private Note"}
                    </span>
                    {isOwner && (
                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                className="hm-edit-prominent-btn"
                                onClick={() => {
                                    if (window.confirm("Are you sure you want to delete this note? This action cannot be undone.")) {
                                        dispatch(deleteHealthMapNote(record.id));
                                    }
                                }}
                                style={{ background: "rgba(255, 0, 0, 0.05)", color: "#ff4d4f", border: "1px solid rgba(255, 0, 0, 0.1)" }}
                            >
                                🗑️ Delete
                            </button>
                            <button
                                className="hm-edit-prominent-btn"
                                onClick={() => setIsEditing(true)}
                            >
                                ✏️ Edit Note
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit / Create view
    return (
        <div className="record-editor-inner">
            <textarea
                className="record-textarea"
                placeholder="Write a clinical note..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                autoFocus
            />
            
            <div className="record-image-upload-zone">
                {imagePreview ? (
                    <div className="record-image-preview-container">
                        <img src={imagePreview} alt="Preview" />
                        <button className="remove-preview-btn" onClick={handleRemoveImage}>✕</button>
                    </div>
                ) : (
                    <div className="upload-placeholder" onClick={() => fileInputRef.current.click()}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                        </svg>
                        <span>Add Image Attachment</span>
                    </div>
                )}
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    style={{ display: "none" }} 
                    accept="image/*" 
                    onChange={handleFileChange}
                />
            </div>

            <div className="record-actions">
                <button className="record-cancel-btn" onClick={handleCancel}>Cancel</button>
                <div style={{ display: "flex", gap: 8 }}>
                    {isNew ? (
                        <>
                            <button
                                className="record-save-btn private"
                                onClick={() => handleSave("private")}
                                disabled={saving || (!content.trim() && !image)}
                            >
                                {saving ? "Saving..." : "Save Private Note"}
                            </button>
                            <button
                                className="record-save-btn collaborative"
                                onClick={() => handleSave("collaborative")}
                                disabled={saving || (!content.trim() && !image)}
                            >
                                {saving ? "Saving..." : "Save Collaborative Note"}
                            </button>
                        </>
                    ) : (
                        <button
                            className="record-save-btn"
                            onClick={() => handleSave()}
                            disabled={saving || (!content.trim() && !image && !imagePreview)}
                        >
                            {saving ? "Saving..." : "Save Changes"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HealthMapNoteEditor;
